import type { Statement } from '@gonfalon/audit-log/types';
import type { ResourceSpecifier } from '@gonfalon/resource-specifiers/ast';
import {
  account,
  agentOptimization,
  aiConfig,
  aiDataset,
  aiModelConfig,
  alert,
  application,
  contextKind,
  domainVerification,
  environment,
  experiment,
  flag,
  holdout,
  integration,
  ipAllowlist,
  layer,
  member,
  metric,
  metricDataSource,
  metricGroup,
  multiArmedBandit,
  observabilityDashboard,
  observabilitySettings,
  payloadFilter,
  project,
  relayProxyConfig,
  releasePipeline,
  releasePolicy,
  repository,
  toString as resourceSpecifierToString,
  role,
  sdkKey,
  segment,
  serviceToken,
  team,
  template,
  view,
  webhook,
} from '@gonfalon/resource-specifiers/ast';
import { parse as resourceSpecifierFromString } from '@gonfalon/resource-specifiers/parser';
import { trackError } from '@gonfalon/telemetry/trackError';
import invariant from 'tiny-invariant';

import { activityURLSchema } from './schema';

/**
 * Query parameter names used for change history functionality.
 * These constants ensure consistency across the application and provide better autocomplete support.
 */
export const CHANGE_HISTORY_QUERY_PARAMS = {
  /** Main activity parameter that indicates change history is active */
  ACTIVITY: 'activity',
  /** Filter for events after a specific timestamp */
  ACTIVITY_AFTER: 'activity-after',
  /** Filter for events before a specific timestamp */
  ACTIVITY_BEFORE: 'activity-before',
  /** Text search query parameter */
  ACTIVITY_Q: 'activity-q',
  /** Sort order parameter (asc/desc) */
  ACTIVITY_SORT: 'activity-sort',
  /** Specific audit entry ID to highlight */
  ACTIVITY_AUDIT_ID: 'activity-auditId',
  /** Resource specification parameter */
  SPEC: 'spec',
  /** Directly renders the details view for a specific audit log entry does not render the list view*/
  ACTIVITY_DETAIL_ONLY: 'activity-detail-only',
  /** Compare history entry ID parameter */
  COMPARE_HISTORY_ENTRY_ID: 'compare-id',
} as const;

export const anyAction = ['*'];

export const anyResource = '*';

export type ActivityDateFilter =
  | { mode: 'mostRecent' }
  | {
      mode: 'range';
      after: Date;
      before: Date;
    };

export type ActivityFiltersParams = {
  after?: Date;
  before?: Date;
  dateFilter?: ActivityDateFilter;
  q?: string;
  critical?: boolean;
  spec?: Statement[];
  sort?: 'asc' | 'desc';
};

export const serializeActivityFilter = (query: ActivityFiltersParams, params: URLSearchParams) => {
  if (query.dateFilter) {
    params.delete(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AFTER);
    params.delete(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_BEFORE);

    if (query.dateFilter.mode === 'range') {
      params.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AFTER, query.dateFilter.after.valueOf().toString());
      params.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_BEFORE, query.dateFilter.before.valueOf().toString());
    }
  } else {
    if (query.after) {
      params.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AFTER, query.after.valueOf().toString());
    }
    if (query.before) {
      params.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_BEFORE, query.before.valueOf().toString());
    }
  }
  if (query.q) {
    params.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_Q, query.q);
  } else {
    params.delete(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_Q);
  }
  if (query.spec) {
    params.set(
      CHANGE_HISTORY_QUERY_PARAMS.SPEC,
      query.spec.map((nextFilter) => encodeURIComponent(JSON.stringify(nextFilter))).toString(),
    );
  }
  if (query.sort) {
    params.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_SORT, query.sort);
  }
  params.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY, 'true');
  return params;
};

/**
 * Checks if the provided URLSearchParams contains change history query parameters.
 * This is useful for determining if navigation should be allowed without blocking
 * when the user is navigating to view change history.
 */
export const hasChangeHistoryParams = (searchParams: URLSearchParams): boolean =>
  searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY) ||
  searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AFTER) ||
  searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_BEFORE) ||
  searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_Q) ||
  searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_SORT) ||
  searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AUDIT_ID) ||
  searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.SPEC);

export const deserializeActivityFilter = (searchParams: URLSearchParams) => {
  const params: ActivityFiltersParams = {};
  const after = searchParams.get(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AFTER);
  if (after) {
    params.after = new Date(Number(after));
  }
  const before = searchParams.get(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_BEFORE);
  if (before) {
    params.before = new Date(Number(before));
  }
  const q = searchParams.get(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_Q);
  if (q) {
    params.q = q;
  }
  const spec = searchParams.get(CHANGE_HISTORY_QUERY_PARAMS.SPEC);
  if (spec) {
    try {
      const nextSpec = spec.split(',').map((s) => JSON.parse(decodeURIComponent(s)));
      const parse = activityURLSchema.safeParse(nextSpec);
      if (parse.success) {
        params.spec = parse.data;
      } else {
        // if we can't parse the spec we fallback to a default spec
        params.spec = [defaultAccountPolicyList(anyAction)];
      }
    } catch (e) {
      if (e instanceof Error) {
        trackError(e, { severity: 'low' });
      }
      // if we can't parse the spec we fallback to a default spec
      params.spec = [defaultAccountPolicyList(anyAction)];
    }
  }
  const sort = searchParams.get(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_SORT);
  if (sort) {
    params.sort = sort as 'asc' | 'desc';
  }
  return params;
};

export const defaultFlagAuditLogPolicyList = (
  projectKey: string,
  environments: string[],
  flagKey: string,
  actions: string[],
  // View keys (REL-13059): when set, the resource carries a `;view:<key>` constraint and the
  // server restricts the listing to the View's current flag members.
  views: string[] = [],
): Statement => ({
  resources: environments.map((env) =>
    resourceSpecifierToString(flag(environment(project(projectKey), env), flagKey, [], views)),
  ),
  effect: 'allow',
  actions,
});

export const defaultSegmentAuditLogPolicyList = (
  projectKey: string,
  environmentKeys: string[],
  segmentKey: string,
  actions: string[],
  views: string[] = [],
): Statement => ({
  resources: [
    resourceSpecifierToString(segment(environment(project(projectKey), environmentKeys[0]), segmentKey, [], views)),
  ],
  effect: 'allow',
  actions,
});

export const defaultMemberAuditLogPolicyList = (memberId: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(member(memberId))],
  effect: 'allow',
  actions,
});

export const defaultTeamsAuditLogPolicyList = (teamKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(team(teamKey))],
  effect: 'allow',
  actions,
});

export const defaultProjectAuditLogPolicyList = (projectKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(project(projectKey))],
  effect: 'allow',
  actions,
});

export const defaultRolePolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(role('*'))],
  effect: 'allow',
  actions,
});

export const defaultAccountPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(account())],
  effect: 'allow',
  actions,
});

export const defaultRelayProxyConfigPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(relayProxyConfig('*'))],
  effect: 'allow',
  actions,
});

export const defaultServiceTokenPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(serviceToken('*'))],
  effect: 'allow',
  actions,
});

export const defaultIpAllowlistPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(ipAllowlist())],
  effect: 'allow',
  actions,
});

export const defaultDomainVerificationPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(domainVerification('*'))],
  effect: 'allow',
  actions,
});

export const defaultWebhookPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(webhook('*'))],
  effect: 'allow',
  actions,
});

export const defaultIntegrationPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(integration('*'))],
  effect: 'allow',
  actions,
});

export const defaultCodeReferenceRepositoryPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(repository('*'))],
  effect: 'allow',
  actions,
});

export const defaultApplicationPolicyList = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(application('*'))],
  effect: 'allow',
  actions,
});

export const defaultReleasePipelinePolicy = (projectKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(releasePipeline(project(projectKey), '*'))],
  effect: 'allow',
  actions,
});

export const defaultReleasePolicyPolicy = (projectKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(releasePolicy(project(projectKey), '*'))],
  effect: 'allow',
  actions,
});

export const defaultContextKindPolicyList = (projectKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(contextKind(project(projectKey), '*'))],
  effect: 'allow',
  actions,
});

export const defaultLayerPolicyList = (projectKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(layer(project(projectKey), '*'))],
  effect: 'allow',
  actions,
});

export const defaultMetricPolicy = (projectKey: string, metricKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(metric(project(projectKey), metricKey))],
  effect: 'allow',
  actions,
});

export const defaultEnviromentPolicy = (projectKey: string, environmentKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(environment(project(projectKey), environmentKey))],
  effect: 'allow',
  actions,
});

export const defaultHoldoutPolicyList = (
  projectKey: string,
  environments: string[],
  holdoutKey: string,
  actions: string[],
): Statement => ({
  resources: environments.map((env) =>
    resourceSpecifierToString(holdout(environment(project(projectKey), env), holdoutKey)),
  ),
  effect: 'allow',
  actions,
});

export const defaultExperimentPolicyList = (
  projectKey: string,
  environments: string[],
  actions: string[],
): Statement => ({
  resources: environments.map((env) =>
    resourceSpecifierToString(experiment(environment(project(projectKey), env), '*')),
  ),
  effect: 'allow',
  actions,
});

export const defaultMultiArmedBanditPolicyList = (
  projectKey: string,
  environments: string[],
  multiArmedBanditKey: string,
  actions: string[],
): Statement => ({
  resources: environments.map((env) =>
    resourceSpecifierToString(multiArmedBandit(environment(project(projectKey), env), multiArmedBanditKey)),
  ),
  effect: 'allow',
  actions,
});

export const defaultPayloadFilterPolicy = (projectKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(payloadFilter(project(projectKey), '*'))],
  effect: 'allow',
  actions,
});

export const defaultMetricGroupPolicy = (projectKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(metricGroup(project(projectKey), '*'))],
  effect: 'allow',
  actions,
});

export const defaultMetricDataSourcePolicyList = (
  projectKey: string,
  environments: string[],
  dataSourceKey: string,
  actions: string[],
): Statement => ({
  resources: environments.map((env) =>
    resourceSpecifierToString(metricDataSource(environment(project(projectKey), env), dataSourceKey)),
  ),
  effect: 'allow',
  actions,
});

export const defaultTemplatePolicy = (actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(template('*'))],
  effect: 'allow',
  actions,
});

export const defaultAIConfigPolicyList = (
  projectKey: string,
  environmentKeys: string[],
  configKey: string,
  actions: string[],
  views: string[] = [],
): Statement => ({
  resources: environmentKeys.map((env) =>
    resourceSpecifierToString(aiConfig(environment(project(projectKey), env), configKey, [], views)),
  ),
  effect: 'allow',
  actions,
});

export const defaultAIModelConfigPolicyList = (
  projectKey: string,
  configKey: string,
  actions: string[],
): Statement => ({
  resources: [resourceSpecifierToString(aiModelConfig(project(projectKey), configKey))],
  effect: 'allow',
  actions,
});

export const defaultAIDatasetPolicyList = (projectKey: string, datasetKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(aiDataset(project(projectKey), datasetKey))],
  effect: 'allow',
  actions,
});

export const defaultViewPolicyList = (projectKey: string, viewKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(view(project(projectKey), viewKey))],
  effect: 'allow',
  actions,
});

// REL-13059: the View link/unlink audit actions to surface per resource kind on the list-surface
// Change History (e.g. linking a flag to a View shows up under `linkFlagToView`). These must match
// the ViewActionLiterals union in @gonfalon/permissions; ChangeHistoryUtils-viewfilter-test guards
// the exact strings against typos.
export const viewLinkActionsByKind = {
  flag: ['linkFlagToView', 'unlinkFlagFromView'],
  segment: ['linkSegmentToView', 'unlinkSegmentFromView'],
  aiConfig: ['linkAIConfigToView', 'unlinkAIConfigFromView'],
} as const;

export type ViewLinkResourceKind = keyof typeof viewLinkActionsByKind;

/**
 * REL-13059: Builds the View resource statement that surfaces a View's link/unlink audit events for
 * a given resource kind, to prepopulate the list-surface Change History filter alongside the
 * resource changes. Returns undefined when no View is selected so callers can spread it
 * conditionally.
 */
export function viewLinkActivityStatement(
  kind: ViewLinkResourceKind,
  projectKey: string,
  viewKey: string | undefined,
): Statement | undefined {
  if (!viewKey) {
    return undefined;
  }
  return {
    resources: [resourceSpecifierToString(view(project(projectKey), viewKey))],
    effect: 'allow',
    actions: [...viewLinkActionsByKind[kind]],
  };
}

export const defaultAlertPolicyList = (projectKey: string, alertKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(alert(project(projectKey), alertKey))],
  effect: 'allow',
  actions,
});

export const defaultAgentOptimizationPolicyList = (
  projectKey: string,
  optimizationKey: string,
  actions: string[],
): Statement => ({
  resources: [resourceSpecifierToString(agentOptimization(project(projectKey), optimizationKey))],
  effect: 'allow',
  actions,
});

export const defaultObservabilityDashboardPolicyList = (
  projectKey: string,
  dashboardKey: string,
  actions: string[],
): Statement => ({
  resources: [
    resourceSpecifierToString(observabilityDashboard(project(projectKey), dashboardKey, [], undefined, undefined)),
  ],
  effect: 'allow',
  actions,
});

export const defaultObservabilitySettingsPolicyList = (projectKey: string, actions: string[]): Statement => ({
  resources: [resourceSpecifierToString(observabilitySettings(project(projectKey), '*'))],
  effect: 'allow',
  actions,
});

export const defaultSdkKeyPolicyList = (
  projectKey: string,
  environmentKey: string,
  sdkKeyKey: string,
  actions: string[],
): Statement => ({
  resources: [resourceSpecifierToString(sdkKey(environment(project(projectKey), environmentKey), sdkKeyKey))],
  effect: 'allow',
  actions,
});

export const addResource = (
  resource: ResourceSpecifier['type'],
  projectKey: string,
  environmentKeys: string[],
): Statement => {
  switch (resource) {
    case 'flag':
      return defaultFlagAuditLogPolicyList(projectKey, environmentKeys, anyResource, anyAction);
    case 'segment':
      return defaultSegmentAuditLogPolicyList(projectKey, environmentKeys, anyResource, anyAction);
    case 'experiment':
      return defaultExperimentPolicyList(projectKey, environmentKeys, anyAction);
    case 'multi-armed-bandit':
      return defaultMultiArmedBanditPolicyList(projectKey, environmentKeys, anyResource, anyAction);
    case 'holdout':
      return defaultHoldoutPolicyList(projectKey, environmentKeys, anyResource, anyAction);
    case 'member':
      return defaultMemberAuditLogPolicyList(anyResource, anyAction);
    case 'team':
      return defaultTeamsAuditLogPolicyList(anyResource, anyAction);
    case 'proj':
      return defaultProjectAuditLogPolicyList(anyResource, anyAction);
    case 'role':
      return defaultRolePolicyList(anyAction);
    case 'acct':
      return defaultAccountPolicyList(anyAction);
    case 'relay-proxy-config':
      return defaultRelayProxyConfigPolicyList(anyAction);
    case 'service-token':
      return defaultServiceTokenPolicyList(anyAction);
    case 'ip-allowlist':
      return defaultIpAllowlistPolicyList(anyAction);
    case 'domain-verification':
      return defaultDomainVerificationPolicyList(anyAction);
    case 'webhook':
      return defaultWebhookPolicyList(anyAction);
    case 'integration':
      return defaultIntegrationPolicyList(anyAction);
    case 'code-reference-repository':
      return defaultCodeReferenceRepositoryPolicyList(anyAction);
    case 'application':
      return defaultApplicationPolicyList(anyAction);
    case 'release-pipeline':
      return defaultReleasePipelinePolicy(projectKey, anyAction);
    case 'release-policy':
      return defaultReleasePolicyPolicy(projectKey, anyAction);
    case 'context-kind':
      return defaultContextKindPolicyList(projectKey, anyAction);
    case 'layer':
      return defaultLayerPolicyList(projectKey, anyAction);
    case 'metric':
      return defaultMetricPolicy(projectKey, anyResource, anyAction);
    case 'metric-data-source':
      return defaultMetricDataSourcePolicyList(projectKey, environmentKeys, anyResource, anyAction);
    case 'payload-filter':
      return defaultPayloadFilterPolicy(projectKey, anyAction);
    case 'metric-group':
      return defaultMetricGroupPolicy(projectKey, anyAction);
    case 'template':
      return defaultTemplatePolicy(anyAction);
    case 'env':
      return defaultEnviromentPolicy(projectKey, environmentKeys[0], anyAction);
    case 'aiconfig':
      return defaultAIConfigPolicyList(projectKey, environmentKeys, anyResource, anyAction);
    case 'ai-model-config':
      return defaultAIModelConfigPolicyList(projectKey, anyResource, anyAction);
    case 'ai-dataset':
      return defaultAIDatasetPolicyList(projectKey, anyResource, anyAction);
    case 'view':
      return defaultViewPolicyList(projectKey, anyResource, anyAction);
    case 'sdk-key':
      return defaultSdkKeyPolicyList(projectKey, environmentKeys[0], anyResource, anyAction);
    case 'agent-optimization':
      return defaultAgentOptimizationPolicyList(projectKey, anyResource, anyAction);
    case 'alert':
      return defaultAlertPolicyList(projectKey, anyResource, anyAction);
    case 'observability-dashboard':
      return defaultObservabilityDashboardPolicyList(projectKey, anyResource, anyAction);
    case 'observability-settings':
      return defaultObservabilitySettingsPolicyList(projectKey, anyAction);
    default:
      return defaultAccountPolicyList(anyAction);
  }
};

export function getFirstResource(s: Statement) {
  const firstResourceSpecifier = s.resources && s.resources.length > 0 ? s.resources[0] : null;

  invariant(firstResourceSpecifier !== null, 'At least one resource specifier is required');

  const parsedFirstResource = resourceSpecifierFromString(firstResourceSpecifier);

  invariant(!parsedFirstResource.err, 'Failed to parse the first resource specifier');

  return parsedFirstResource;
}

export function getEnvironments(s: Statement) {
  invariant(s.resources && s.resources.length > 0, 'Resources must be defined');
  return s.resources.map((r) => {
    const resourceItem = resourceSpecifierFromString(r);
    if (resourceItem.err) {
      throw new Error(resourceItem.val.message);
    }
    if (resourceItem.val.type === 'flag') {
      return resourceItem.val.environment.name;
    }
    return '*';
  });
}

/**
 * Returns the View keys constraining a flag/segment/AI config statement (REL-13059, inline View
 * filter). Empty when the statement has no `;view:` constraint. Reflects the first resource, which
 * is sufficient because a single filter row's resources all share the same View selection.
 */
export function getViews(s: Statement): string[] {
  const resource = getFirstResource(s);
  if ('views' in resource.val && Array.isArray(resource.val.views)) {
    return resource.val.views;
  }
  return [];
}
